// Apollo

import { ApolloClient } from 'apollo-boost';
import { createHttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { withClientState } from 'apollo-link-state';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import history from 'utils/history';
import store from 'redux/store';
import * as Sentry from '@sentry/react';
import { normalizeLocale } from 'utils/localeHelper';
import { toUiLanguage } from 'utils/locale/language';
import sessionManager, { END_REASON } from 'utils/session';
import { redactSensitiveData } from 'utils/redactSensitiveData';

// Constants
import constants from "config/constants";
const { API_URI, AUTH_TOKEN } = constants;

class GrapQLError extends Error {
  constructor(message, operationName) {
    super(message);
    this.name = `[GraphQLError] ${operationName}`;
  }
}

const httpLink = createHttpLink({
  // uri: `https://api.c1do1.advancedlapp.com/graphql`,
  uri: `${API_URI}/graphql`,
  //credentials: 'include'
});

const authLink = setContext((_, { headers }) => {

  //const token = Cookies.get(AUTH_TOKEN);
  const token = localStorage.getItem(AUTH_TOKEN);

  // get the authentication token from local storage if it exists
  const locale = normalizeLocale(toUiLanguage(localStorage.getItem("locale")));

  // return the headers to the context so httpLink can read them
  return { headers: { authorization: token, locale: locale } };
});

const cache = new InMemoryCache({});

const defaultState = {
  searchText: "",
  categoryFilter: null,
};


const errorLink = onError(({ operation, networkError, graphQLErrors, forward }) => {
  if (!networkError && !graphQLErrors) return;

  let authNeeded = false;
  const query = {
    operationName: operation.operationName,
    variables: redactSensitiveData(operation.variables)
  };

  if (graphQLErrors) {
    graphQLErrors.forEach(({ code, message }) => {
      // Log detallado para debugging (solo en desarrollo)
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[GraphQL Error] ${query.operationName}:`, {
          code,
          message,
          variables: query.variables
        });
      }
      
      Sentry.captureException(
        new GrapQLError(message, query.operationName),
        {
          contexts: {
            query: { ...query, code }
          }
        });
      if (code === 'AUTHENTICATION_REQUIRED')
        authNeeded = true;
    });
  }
  
  if (networkError) {
    // Extraer información del error de red
    const statusCode = networkError.statusCode || (networkError.response && networkError.response.status);
    const errorMessage = networkError.message || 'Network error';
    
    // Log detallado para debugging (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[Network Error] ${query.operationName}:`, {
        statusCode,
        message: errorMessage,
        variables: query.variables
      });
    }
    
    // Enviar a Sentry con información adicional
    // Apollo network errors can retain the original request body. Capture a
    // fresh error plus explicitly redacted context so recovery credentials can
    // never travel with the exception object.
    Sentry.captureException(new Error(errorMessage), {
      contexts: {
        query: {
          ...query,
          statusCode,
          errorMessage
        }
      },
      tags: {
        errorType: 'network',
        statusCode: statusCode?.toString() || 'unknown'
      }
    });
    
    // Para errores 500, 502, 503, 504 (errores del servidor), no hacer nada adicional
    // Solo registrar en Sentry. Los componentes deben manejar estos errores con error boundaries
    if (statusCode && [500, 502, 503, 504].includes(statusCode)) {
      // No hacer nada adicional, solo registrar
      return;
    }
  }

  if (authNeeded) {
    // Inside LTI the user is in an iframe hosted by Blackboard and cannot sign
    // in again there, so that case keeps its own destination and never reaches
    // the session manager's sign-out.
    const ltiPlatform = store.getState()['general'].ltiPlatform;
    if (ltiPlatform) {
      localStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem('user_id');
      localStorage.removeItem('logged_date');
      localStorage.removeItem('course_id');
      history.push({ pathname: '/courses/error', search: 'expiredSession' });
      history.go();
      return;
    }

    // Everything else defers to the one authority. end() is idempotent, so the
    // six queries a dashboard has in flight when a session dies produce one
    // sign-out — not six redirects, each followed by a full page reload.
    sessionManager.end(END_REASON.UNAUTHENTICATED);
  }
});

const stateLink = withClientState({
  cache,
  defaults: defaultState,
  resolvers: {
    Mutation: {
      updateCategoryFilter: (_, { newCategoryFilter }, { cache }) => {
        cache.writeData({ data: { categoryFilter: newCategoryFilter } });
      },
      updateSearchText: (_, { newSearchText }, { cache }) => {
        cache.writeData({ data: { searchText: newSearchText } });
      },
    },
  },
});

const client = new ApolloClient({
  link: ApolloLink.from([stateLink, errorLink, authLink.concat(httpLink)]),
  cache,
});

export default client;
